/**
 * Learner profile: the small set of things a learner can change about their own
 * account. Email and photo come from the identity provider and are read-only
 * here — editing them belongs in the Google account, not in ours.
 */

import { cn } from '../lib/utils';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { LearnRoot, LearnTopBar, useLearnTheme, glassPanel, inkMuted } from '../components/learn/learnShared';
import { LearnAccountMenu } from '../components/learn/LearnAccountMenu';
import { useLearnAuth } from '../components/learn/LearnAuthProvider';
import { updateLearnerName } from '../services/learnerAPI';
import { SchoolCodeForm } from '../components/learn/SchoolCodeForm';

export const Route = createFileRoute('/learn/profile')({ component: LearnProfile });

function LearnProfile() {
  const { theme, toggle } = useLearnTheme();
  const { profile, refresh, signOut } = useLearnAuth();
  const [name, setName] = useState(profile?.displayName ?? '');
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [error, setError] = useState<string | null>(null);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    setState('saving');
    setError(null);
    try {
      await updateLearnerName(name);
      await refresh();
      setState('saved');
      setTimeout(() => setState('idle'), 2000);
    } catch {
      setError('Could not save your name. Try again.');
      setState('idle');
    }
  };

  return (
    <LearnRoot theme={theme} subjects={[]}>
      <LearnTopBar theme={theme} onToggle={toggle} right={<LearnAccountMenu />} />
      <div className="mx-auto max-w-[580px] px-6 py-12">
        <p className={cn('text-[11px] font-bold uppercase tracking-[0.16em]', inkMuted)}>Your account</p>
        <h1 className="mt-1 font-serif text-[36px] leading-[1.14] tracking-tight">Profile</h1>

        <form onSubmit={save} className={cn(glassPanel, 'mt-6 flex flex-col gap-5 p-6')}>
          <label className="flex flex-col gap-2">
            <span className={cn('text-[11px] font-bold uppercase tracking-[0.12em]', inkMuted)}>
              Display name
            </span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
              placeholder="How should we greet you?"
              className="rounded-xl border border-white/70 bg-white/85 px-4 py-3 text-sm outline-none focus:border-[#5B2EE5] dark:border-white/12 dark:bg-white/[0.06] dark:text-[#EFEEF8]"
            />
          </label>

          <label className="flex flex-col gap-2">
            <span className={cn('text-[11px] font-bold uppercase tracking-[0.12em]', inkMuted)}>
              Email
            </span>
            <input
              value={profile?.email ?? ''}
              readOnly
              className={cn("cursor-not-allowed rounded-xl border border-white/60 bg-white/55 px-4 py-3 text-sm dark:border-white/10 dark:bg-white/[0.04]", inkMuted)}
            />
          </label>

          {error && <p className="text-sm text-[#B4341F] dark:text-[#FF8A73]">{error}</p>}

          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={state === 'saving' || !name.trim()}
              className="rounded-xl bg-[#5B2EE5] px-5 py-2.5 text-sm font-bold text-white shadow-[0_10px_28px_-12px_rgba(91,46,229,0.9)] hover:brightness-110 disabled:opacity-60"
            >
              {state === 'saving' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </button>
            {state === 'saved' && (
              <span className="flex items-center gap-1.5 text-sm font-semibold text-[#0E8A6B] dark:text-[#3DDBB0]">
                <Check className="h-4 w-4" /> Saved
              </span>
            )}
          </div>
        </form>

        <div className={cn(glassPanel, 'mt-6 p-6')}>
          {profile?.school ? (
            <>
              <p className={cn('text-[11px] font-bold uppercase tracking-[0.12em]', inkMuted)}>School</p>
              <p className="mt-2 text-sm font-semibold">{profile.school.name}</p>
            </>
          ) : (
            <>
              <SchoolCodeForm />
              <p className={cn('mt-3 text-xs leading-relaxed', inkMuted)}>
                Entering your school's code adds the lessons your school has chosen for you.
              </p>
            </>
          )}
        </div>

        <div className="mt-12 border-t border-black/[0.08] pt-6 dark:border-white/10">
          <button
            onClick={() => void signOut()}
            className={cn("text-sm font-medium hover:text-[#5B2EE5] dark:hover:text-[#BFA8FA]", inkMuted)}
          >
            Sign out
          </button>
          <p className="mt-4 text-xs leading-relaxed text-[#8A8593]">
            To delete your account and learning history, email{' '}
            <a href="mailto:support@4klabs.ai" className="underline hover:text-[#5B2EE5] dark:hover:text-[#BFA8FA]">
              support@4klabs.ai
            </a>
            .
          </p>
        </div>
      </div>
    </LearnRoot>
  );
}
